import React, { useEffect, useMemo, useState } from 'react';

// Components
import * as S from './Styled';
import Icon from '../Icon';
import Spinner from '../Spinner';
import * as Label from '../Label';
import CountryFlag from '../CountryFlag';
import Buttons from './Buttons';

// Utils
import { useHistory } from 'react-router-dom';
import api from '../../api';
import _indexOf from 'lodash/indexOf';
import { useTranslation } from 'react-i18next';
import {
  canBuy,
  getPackingName,
  isDisabledForPurchase,
} from '../../utils/product';

// Store
import { addToFavorites } from '../../actions';
import { useSelector, useDispatch } from 'react-redux';
import { transformToLowercase } from '../../utils';
import { isDivisible } from '../../utils/cart';
import { useLang } from '../../context/lang';

const Item = ({ closeModal, initialItem, addToCart, offerId = null }) => {
  const [item, setItem] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const location = useSelector(state => state.user.location);
  const history = useHistory();
  const dispatch = useDispatch();
  const favorites = useSelector(state => state.user.favorites);
  const isFavor = useMemo(() => {
    if (item !== null) {
      return _indexOf(favorites, item.id) + 1;
    }
  }, [favorites, item]);
  const { t } = useTranslation();
  const { language } = useLang();

  useEffect(() => {
    const loadItem = async () => {
      try {
        const { data } = await api.get(
          `/offers/${offerId}/goods/${initialItem.id}`,
          {
            params: {
              latitude: location ? location.latitude : null,
              longitude: location ? location.longitude : null,
              radius: 3000,
            },
          }
        );

        if (data.online_price === null) data.online_price = 0;
        if (data.drugstore_price === null) data.drugstore_price = 0;
        setItem(data);
        setLoading(false);
      } catch (e) {
        //Keep in mind that error responses handled by axios inter
        closeModal();
        setItem(null);
        console.error(e);
      }
    };

    setLoading(true);

    if (initialItem) {
      setItem(initialItem);
      loadItem();
    } else {
      setItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItem]);

  return (
    <S.Wrapper>
      {item ? (
        <>
          <S.InnerWrapper>
            <S.Header>
              <S.Breadcrumbs>
                {item?.goods_atc_class_tree?.map((i, k) => (
                  <S.BreadcrumbItem
                    to={`/catalog/${i.atc_code}`}
                    key={k}
                    onClick={closeModal}
                  >
                    <S.BreadcrumbLabel>
                      {transformToLowercase(i.name)}
                    </S.BreadcrumbLabel>
                    {item.goods_atc_class_tree.length - 1 > k && (
                      <Icon name="right" />
                    )}
                  </S.BreadcrumbItem>
                ))}
                {item?.goods_name?.name?.name ? (
                  <S.BreadcrumbItem
                    to={`/catalog/${encodeURI(item.name)}`}
                    onClick={closeModal}
                  >
                    <Icon name="right" />
                    <S.BreadcrumbLabel>
                      {transformToLowercase(item?.goods_name?.name?.name)}
                    </S.BreadcrumbLabel>
                  </S.BreadcrumbItem>
                ) : null}
              </S.Breadcrumbs>
              <S.Title>{item[`name_${language}`]}</S.Title>
            </S.Header>
            <S.Content>
              <S.ImgCol>
                <S.Photo>
                  <S.LabelsContainer>
                    {item?.in_medication_program ? (
                      <Label.Normal msgTitle="Доступні ліки" msgText="">
                        Доступні ліки
                      </Label.Normal>
                    ) : null}
                    {item?.is_recipe ? (
                      <Label.Important
                        msgTitle={t('labels.only_by_recipe')}
                        msgText=""
                      >
                        {t('labels.only_by_recipe')}
                      </Label.Important>
                    ) : null}
                  </S.LabelsContainer>

                  <S.Wishlist onClick={() => dispatch(addToFavorites(item.id))}>
                    <Icon
                      style={{ marginRight: 9 }}
                      name="heart"
                      className={isFavor ? 'icon_filled' : ''}
                      title={t('labels.add_to_favorites')}
                    />
                    <S.WishlistTitle>
                      {isFavor
                        ? t('labels.in_favorites')
                        : t('labels.add_to_favorites')}
                    </S.WishlistTitle>
                  </S.Wishlist>
                  <img
                    src={
                      item.image_url?.length
                        ? `${item.image_url?.[0]}?w=400&h=400&q=80`
                        : '/assets/images/no_photo.jpg'
                    }
                    alt={
                      item.image_url?.length ? item.name : t('labels.no_photo')
                    }
                  />
                </S.Photo>
              </S.ImgCol>
              <S.Col>
                <S.Details>
                  <S.Line>
                    <S.Capt>{t('labels.producer')}</S.Capt>
                    <S.Producer>
                      <CountryFlag code={item.goods_producer?.country_code} />{' '}
                      {item.goods_producer?.name}
                    </S.Producer>
                  </S.Line>
                  <S.Line>
                    <S.Capt>{t('labels.price')}</S.Capt>
                    {!isLoading ? (
                      <>
                        {item?.online_price === 0 ||
                        item?.drugstore_price === 0 ? (
                          <S.NotAvailable>
                            {t('labels.not_available')}
                          </S.NotAvailable>
                        ) : item?.online_price === item?.drugstore_price ? (
                          <S.Price>
                            {item?.online_price?.toFixed(2)} ₴
                            <S.Hint>
                              {t(
                                'labels.the_price_depends_on_the_selected_pharmacy'
                              )}
                            </S.Hint>
                          </S.Price>
                        ) : (
                          <S.Price>
                            {item?.online_price?.toFixed(2)} ₴ -{' '}
                            {item?.drugstore_price?.toFixed(2)} ₴
                            <S.Hint>
                              {t(
                                'labels.the_price_depends_on_the_selected_pharmacy'
                              )}
                            </S.Hint>
                          </S.Price>
                        )}
                      </>
                    ) : (
                      <Spinner />
                    )}
                  </S.Line>
                  <S.Line>
                    <S.Message>
                      {isDivisible(item.pack_info) &&
                      item.pack_info.quantity_unit_in_pack > 1 ? (
                        <>
                          <S.Icon>
                            <Icon
                              name={getPackingName(item.pack_info.unit_name)}
                            />{' '}
                          </S.Icon>
                          <S.Text>
                            {t(
                              'labels.in_the_selected_dosage_it_is_possible_to_purchase_individual_see_on_the_track_step',
                              {
                                packing: t(
                                  `labels.${item.pack_info.unit_name}s`
                                ),
                              }
                            )}
                          </S.Text>
                        </>
                      ) : null}
                    </S.Message>
                  </S.Line>
                  {canBuy(item) ? (
                    <S.ButtonsCont>
                      <Buttons
                        isDisabled={isDisabledForPurchase(item)}
                        addToCart={() => addToCart(item)}
                        isLoading={isLoading}
                        history={history}
                        item={item}
                        selectedProduct={item}
                      />
                    </S.ButtonsCont>
                  ) : null}
                </S.Details>
              </S.Col>
            </S.Content>
          </S.InnerWrapper>
          {canBuy(item) ? (
            <S.Footer>
              <Buttons
                isDisabled={isDisabledForPurchase(item)}
                addToCart={() => addToCart(item)}
                isLoading={isLoading}
                history={history}
                item={item}
                selectedProduct={item}
              />
            </S.Footer>
          ) : null}
        </>
      ) : null}
    </S.Wrapper>
  );
};

export default Item;
